/**
 * KMMO kit: pure helpers (no DOM). Number formatting, radar geometry, success-rate tones,
 * enhancement labels, stage framing maths. Tested in helpers.test.ts.
 */

export type KmRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
export const KM_RARITIES: readonly KmRarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

export const clamp01 = (v: number): number => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

/** "128,420" (rounded, comma groups, keeps the sign). */
export function fmtInt(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const s = String(Math.round(Math.abs(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return n < 0 && Math.round(Math.abs(n)) !== 0 ? `-${s}` : s;
}

/**
 * Currency-bar number: full digits up to 9,999,999 (the reference shows "9,999,999"), then
 * "12.4M" / "1.2B".
 */
export function fmtCurrency(n: number): string {
  const a = Math.abs(n);
  if (a < 10_000_000) return fmtInt(n);
  const [d, s] = a >= 1e9 ? [1e9, 'B'] : [1e6, 'M'];
  const v = Math.floor((n / d) * 10) / 10;
  return `${v.toFixed(v % 1 === 0 ? 0 : 1)}${s}`;
}

/** "70%" for 0.7; `digits` adds decimals ("18.4%"). Input is a fraction. */
export function fmtPercent(frac: number, digits = 0): string {
  const v = (Number.isFinite(frac) ? frac : 0) * 100;
  return `${v.toFixed(digits)}%`;
}

/** "+7" for an enhancement level; '' for 0 / missing. */
export function plusLabel(n: number | null | undefined): string {
  return n && n > 0 ? `+${Math.floor(n)}` : '';
}

export type ChanceTone = 'high' | 'mid' | 'low';

/** Colour band for a success chance (fraction): ≥ 0.7 green, ≥ 0.4 amber, else red. */
export function chanceTone(frac: number): ChanceTone {
  if (frac >= 0.7) return 'high';
  if (frac >= 0.4) return 'mid';
  return 'low';
}

export type DeltaTone = 'up' | 'down' | 'same';
export function deltaTone(from: number, to: number): DeltaTone {
  return to > from ? 'up' : to < from ? 'down' : 'same';
}

/** Value / max as a 0..1 fraction (0 when max ≤ 0). */
export function frac(value: number, max: number): number {
  return max > 0 ? clamp01(value / max) : 0;
}

// ---------------------------------------------------------------------------
// Radar chart geometry
// ---------------------------------------------------------------------------

export interface Pt {
  x: number;
  y: number;
}

/**
 * Vertex `i` of `n` on a circle of radius `r` around (cx, cy); vertex 0 points straight up,
 * then clockwise.
 */
export function radarVertex(i: number, n: number, cx: number, cy: number, r: number): Pt {
  const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
  return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r };
}

/** Polygon points for values (each clamped 0..1) at radius `r`. */
export function radarPolygon(values: readonly number[], cx: number, cy: number, r: number): Pt[] {
  const n = values.length;
  return values.map((v, i) => radarVertex(i, n, cx, cy, r * clamp01(v)));
}

/** SVG `points` attribute ("x,y x,y …", 2 decimals). */
export function pointsAttr(pts: readonly Pt[]): string {
  return pts.map((p) => `${round2(p.x)},${round2(p.y)}`).join(' ');
}

/** Text anchor for a label at vertex `i` of `n` (left / middle / right of the point). */
export function radarAnchor(i: number, n: number): 'start' | 'middle' | 'end' {
  const x = Math.cos(-Math.PI / 2 + (i * 2 * Math.PI) / n);
  if (x > 0.2) return 'start';
  if (x < -0.2) return 'end';
  return 'middle';
}

const round2 = (v: number): number => Math.round(v * 100) / 100;

// ---------------------------------------------------------------------------
// Hero stage framing (heroStage.ts)
// ---------------------------------------------------------------------------

export interface StageFrameInput {
  /** Canvas aspect (width / height). */
  aspect: number;
  /** Vertical field of view (degrees). */
  fovDeg: number;
  /** Hero height (world units). */
  heroHeight: number;
  /** Fraction of the canvas height the hero should fill (0.2..1). */
  fill: number;
  /** Where the feet sit, as a fraction of the canvas height from the top (0.5..1). */
  feetY: number;
  /** Width the lineup needs (world units); 0 for one hero. */
  lineupWidth: number;
}

export interface StageFrame {
  /** Camera distance from the hero plane. */
  dist: number;
  /** World y the camera looks at (the view's vertical centre). */
  targetY: number;
}

/** Camera distance + aim so the hero fills `fill` of the height with the feet at `feetY`. */
export function stageFrame(f: StageFrameInput): StageFrame {
  const fill = Math.min(1, Math.max(0.2, f.fill));
  const feetY = Math.min(1, Math.max(0.5, f.feetY));
  const t = Math.tan(((f.fovDeg * Math.PI) / 180) / 2);
  let visH = f.heroHeight / fill;
  // Wide lineups: make sure the row fits horizontally (5% margin each side).
  if (f.lineupWidth > 0 && f.aspect > 0) visH = Math.max(visH, (f.lineupWidth * 1.1) / f.aspect);
  const dist = visH / 2 / t;
  return { dist, targetY: (feetY - 0.5) * visH };
}

/** X positions for `n` heroes spaced `gap` apart, centred on 0. */
export function lineupX(n: number, gap: number): number[] {
  return Array.from({ length: Math.max(0, n) }, (_, i) => (i - (n - 1) / 2) * gap);
}
